import { useParams, Link } from 'react-router-dom';
import { useEffect } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import VideoCard from '@/components/base/VideoCard';
import { videos } from '@/mocks/videos';
import { categorySlugMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = videos.find((v) => v.slug === slug);

  if (!article) {
    return (
      <>
        <SEOHead
          title="内容未找到 - 蜜桃tv"
          description="抱歉，您要查看的内容不存在或已被移除。欢迎浏览蜜桃tv其他精选内容。"
          keywords="蜜桃tv,蜜桃tv官网"
          canonicalUrl="https://tjkangkai.cn"
        />
        <div className="min-h-screen bg-background-50">
          <Navbar />
          <ScrollToTop />
          <main className="pt-24 pb-16 text-center">
            <div className="max-w-[600px] mx-auto px-4">
              <span className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-6">
                <i className="ri-emotion-sad-line text-3xl text-foreground-400"></i>
              </span>
              <h1 className="text-2xl font-heading font-bold text-foreground-950 mb-3">
                内容未找到
              </h1>
              <p className="text-foreground-600 mb-6">
                抱歉，您要查看的内容不存在或已被移除
              </p>
              <Link
                to="/"
                className="inline-flex px-6 py-2.5 rounded-full bg-primary-500 text-background-50 font-medium text-sm hover:bg-primary-600 transition-colors"
              >
                返回首页
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      </>
    );
  }

  const relatedVideos = videos
    .filter(
      (v) =>
        v.categorySlug === article.categorySlug && v.id !== article.id
    )
    .slice(0, 4);

  const categoryName = categorySlugMap[article.categorySlug] || article.category;

  const articleSchema = [
    {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.summary,
      image: article.thumbnail,
      author: {
        '@type': 'Organization',
        name: article.author,
      },
      publisher: {
        '@type': 'Organization',
        name: '蜜桃tv',
        logo: {
          '@type': 'ImageObject',
          url: 'https://tjkangkai.cn/logo.png',
        },
      },
      datePublished: article.publishDate,
      url: `https://tjkangkai.cn/article/${article.slug}`,
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://tjkangkai.cn/article/${article.slug}`,
      },
    },
  ];

  return (
    <>
      <SEOHead
        title={`${article.title} - 蜜桃tv`}
        description={`${article.summary} | 蜜桃tv精选${categoryName}内容，高清流畅在线观看。`}
        keywords={`蜜桃tv,${article.title},蜜桃tv ${categoryName},${article.tags.join(',')},蜜桃tv高清`}
        canonicalUrl={`https://tjkangkai.cn/article/${article.slug}`}
        ogType="article"
        ogImage={article.thumbnail}
        schemaData={articleSchema}
      />

      <div className="min-h-screen bg-background-50">
        <Navbar />
        <ScrollToTop />

        <main className="pt-16">
          {/* Breadcrumb */}
          <div className="max-w-[900px] mx-auto pt-4">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                {
                  label: categoryName,
                  href: `/category/${article.categorySlug}`,
                },
                { label: article.title },
              ]}
            />
          </div>

          {/* Article Content */}
          <article className="w-full px-4 md:px-6 py-8">
            <div className="max-w-[900px] mx-auto">
              {/* Header */}
              <header className="mb-8">
                <div className="flex items-center gap-2 mb-4">
                  <Link
                    to={`/category/${article.categorySlug}`}
                    className="px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium hover:bg-primary-200 transition-colors"
                  >
                    {categoryName}
                  </Link>
                  {article.tags.slice(0, 2).map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                  {article.title}
                </h1>
                <div className="flex items-center gap-4 text-sm text-foreground-500 flex-wrap">
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-user-line text-xs"></i>
                    </span>
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-calendar-line text-xs"></i>
                    </span>
                    {article.publishDate}
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-eye-line text-xs"></i>
                    </span>
                    {article.views >= 10000
                      ? `${(article.views / 10000).toFixed(0)}万`
                      : article.views}{' '}
                    次观看
                  </span>
                </div>
              </header>

              {/* Featured Image */}
              <figure className="mb-8 rounded-xl overflow-hidden">
                <img
                  src={article.thumbnail}
                  alt={article.title}
                  className="w-full aspect-video object-cover"
                  title={`${article.title} - 蜜桃tv`}
                />
              </figure>

              {/* Content Body */}
              <div
                className="prose prose-lg max-w-none text-foreground-800 leading-relaxed"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              {/* Stats Bar */}
              <div className="flex items-center gap-6 mt-10 pt-8 border-t border-background-200/70">
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-primary-100 hover:text-primary-600 transition-all duration-200 cursor-pointer">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-heart-line"></i>
                  </span>
                  {article.likes >= 10000
                    ? `${(article.likes / 10000).toFixed(1)}万`
                    : article.likes}{' '}
                  赞
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-primary-100 hover:text-primary-600 transition-all duration-200 cursor-pointer">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-star-line"></i>
                  </span>
                  评分 {article.rating}
                </button>
                <button className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-primary-100 hover:text-primary-600 transition-all duration-200 cursor-pointer">
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-share-forward-line"></i>
                  </span>
                  分享
                </button>
              </div>
            </div>
          </article>

          {/* Related Videos */}
          {relatedVideos.length > 0 && (
            <section className="w-full px-4 md:px-6 py-12 bg-background-100">
              <div className="max-w-[1280px] mx-auto">
                <div className="flex items-center gap-3 mb-6">
                  <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                  <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-950">
                    相关推荐
                  </h2>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                  {relatedVideos.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* Back to category */}
          <div className="w-full px-4 md:px-6 pb-12">
            <div className="max-w-[900px] mx-auto text-center">
              <Link
                to={`/category/${article.categorySlug}`}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-background-100 text-foreground-700 text-sm font-medium hover:bg-primary-100 hover:text-primary-600 transition-all duration-200"
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-xs"></i>
                </span>
                返回{categoryName}频道
              </Link>
            </div>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}